import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  DoctorName,
  GreetingMessage,
  Title,
  ClearSearchButton,
  ResultsQuantity,
  TableHeader,
  Table,
  TableRow,
  TableCell,
  TableBody,
  BlockedTableRow
} from './styles'
import { SearchError } from './Error'
import { Loading } from './Loading'
import { Default } from './Default'
import { GenericSearch, IGenericSearchHandle, ModalProps, Pagination } from '~/components/shared'
import { queryClient, usePatient } from '~/context'
import { IGetPatientParams, IGetPatientsParams, IPatient, User } from '~/interfaces'
import { getAgeByBirthDate, getGreeting, transformDate } from '~/utils'
import { message } from '~/services'
import { DEFAULT_PATH, PATIENT_BLOCKED_NAME, TIMELINE_GENERAL_PATH, searchPatientsDefaultParams } from '~/constants'
import { LocalStorageManager } from '~/modules'
import { IconComponent } from '~/assets/icons/component'
import { Divider, Inline, Stack, theme } from '~/styles'
import { PortalModal } from '~/components/shared/PortalModal'
import { useFetchPatient, useFetchPatients } from '~/hooks'

const Greeting = () => {
  const sessionData = LocalStorageManager.ReadEncryptedData<User>('session')
  const loggedInDoctorName = sessionData?.objeto?.[0]?.NomeUsuario
  const greetingMessage = useMemo(() => getGreeting(new Date().getHours()), [])

  return (
    <GreetingMessage data-testid="greeting">
      {`${greetingMessage}, `} <DoctorName>{loggedInDoctorName}</DoctorName>
    </GreetingMessage>
  )
}

const SearchResultsMetrics = ({
  foundPatients,
  clearSearchedPatients
}: {
  foundPatients: number
  clearSearchedPatients: VoidFunction
}) => {
  const foundResultsLabel = foundPatients > 1 ? 'resultados encontrados' : 'resultado encontrado'
  return (
    <Inline
      width="100%"
      justifyContent="space-between"
      marginTop="0.5rem"
      visibility={foundPatients ? 'visible' : 'hidden'}
    >
      <ResultsQuantity>{`${foundPatients} ${foundResultsLabel}`}</ResultsQuantity>

      <ClearSearchButton onClick={clearSearchedPatients}>
        Limpar busca
        <IconComponent name="close" iconsize={8} />
      </ClearSearchButton>
    </Inline>
  )
}

interface PatientRowProps {
  patient: IPatient
  onAccessPatient: (patientId: string) => void
}

const PatientRow = ({ patient, onAccessPatient }: PatientRowProps): JSX.Element => {
  const getFirstLetterOfBiologicalSex = (biologicalSex: string) => biologicalSex.charAt(0).toUpperCase()

  return (
    <TableRow onClick={() => onAccessPatient(patient.medicalRecord)}>
      <TableCell>{patient.medicalRecord}</TableCell>
      <TableCell>{patient.name}</TableCell>
      <TableCell>{getFirstLetterOfBiologicalSex(patient.biologicalSex)}</TableCell>
      <TableCell>{getAgeByBirthDate(patient.birthDate)}</TableCell>
      <TableCell>{transformDate({ date: patient.birthDate })}</TableCell>
      <TableCell>
        <Stack alignItems="flex-start" paddingLeft="1rem">
          <IconComponent name="chevronRight" iconsize={16} />
        </Stack>
      </TableCell>
    </TableRow>
  )
}

interface BlockedPatientRowProps {
  patient: IPatient
}

const BlockedPatientRow = ({ patient }: BlockedPatientRowProps): JSX.Element => (
  <BlockedTableRow>
    <TableCell>{'-'}</TableCell>
    <TableCell>{patient.name}</TableCell>
    <TableCell>{'-'}</TableCell>
    <TableCell>{'-'}</TableCell>
    <TableCell>{'-'}</TableCell>
    <TableCell>{'-'}</TableCell>
  </BlockedTableRow>
)

interface PatientsListProps {
  patients: IPatient[]
  onAccessPatient: (patientId: string) => void
}

const PatientsList = ({ patients, onAccessPatient }: PatientsListProps) => {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableCell>Prontuário</TableCell>
          <TableCell>Nome</TableCell>
          <TableCell>Sexo</TableCell>
          <TableCell>Idade</TableCell>
          <TableCell>Nascimento</TableCell>
          <TableCell>Visualizar</TableCell>
        </TableRow>
      </TableHeader>

      <TableBody>
        {patients.map(patient =>
          patient.name === PATIENT_BLOCKED_NAME ? (
            <BlockedPatientRow key={Date.now()} patient={patient} />
          ) : (
            <PatientRow key={patient.id} patient={patient} onAccessPatient={onAccessPatient} />
          )
        )}
      </TableBody>
    </Table>
  )
}

interface ISearchPatientModalProps extends Omit<ModalProps, 'children'> {}

export const SearchPatientModal: React.FC<ISearchPatientModalProps> = ({ ...rest }: ISearchPatientModalProps) => {
  const navigate = useNavigate()
  const { errorPatient, resetPatient } = usePatient()
  const inputRef = useRef<IGenericSearchHandle>(null)
  const pageRef = useRef('1')

  const [searchPatientsParams, setSearchPatientsParams] = useState<IGetPatientsParams | null>(null)
  const { data: patients, error: patientsError, isFetching: isPatientsLoading } = useFetchPatients(searchPatientsParams)

  const [searchPatientParams, setSearchPatientParams] = useState<IGetPatientParams | null>(null)
  const { data: patient, error: patientError, isFetching: isPatientLoading } = useFetchPatient(searchPatientParams)

  const patientsFound = useMemo(() => {
    if (patients?.data?.length) return patients.data
    if (patient) return [patient]
    return []
  }, [patient, patients])

  const isLoading = isPatientsLoading || isPatientLoading
  const error = patientError || patientsError || errorPatient
  const isIdle = !error && !isLoading && !patient && !patients?.data.length

  const navigateToPatient = useCallback(
    (medicalRecord: string) => {
      resetPatient()
      navigate(`/${DEFAULT_PATH}/${TIMELINE_GENERAL_PATH}/${medicalRecord}`)
    },
    [navigate, resetPatient]
  )

  useEffect(() => {
    const storageMedicalRecord = LocalStorageManager.ReadEncryptedData('episode_number-encrypted')
    if (storageMedicalRecord) {
      navigateToPatient(String(storageMedicalRecord))
    }
  }, [navigateToPatient])

  const clearStoredSearches = () => {
    resetPatient()
    pageRef.current = '1'
    queryClient.setQueriesData(['patients', searchPatientsParams], undefined)
    queryClient.setQueriesData(['patient', searchPatientParams], undefined)
    setSearchPatientsParams(null)
    setSearchPatientParams(null)
  }

  const clearSearchedPatient = () => {
    inputRef.current?.clear()
    clearStoredSearches()
  }

  const handleSearch = (searchInput: string, field: string) => {
    clearStoredSearches()
    if (!searchInput) return

    if (field === 'name') {
      setSearchPatientsParams({ queryParams: { name: searchInput, ...searchPatientsDefaultParams } })
    }
    if (field === 'medicalRecord') {
      setSearchPatientParams({ pathParams: { medicalRecord: +searchInput } })
    }
  }

  const handlePage = (pageNumber: string) => {
    if (searchPatientsParams) {
      pageRef.current = pageNumber
      const nextPage = String((Number(pageNumber) - 1) * Number(searchPatientsParams.queryParams.limit))
      setSearchPatientsParams({
        queryParams: {
          ...searchPatientsParams.queryParams,
          offset: nextPage
        }
      })
    }
  }

  return (
    <PortalModal
      size={rest.size ?? 'medium'}
      useMask={rest.useMask ?? true}
      modalBodyPadding="0rem"
      modalDialogProps={{ style: { padding: '1.5rem' } }}
      headerPaddingCloseButton="0"
      headerMarginBottom="0"
      {...rest}
    >
      <Container>
        <Stack alignItems="flex-start" width="100%" spacing={0}>
          <Greeting />
          <Title>
            Pesquise por seus pacientes pelo <b>nome</b> ou <b>prontuário</b>
          </Title>
        </Stack>

        <GenericSearch
          ref={inputRef}
          fields={[
            { label: 'Prontuário', value: 'medicalRecord' },
            { label: 'Nome', value: 'name' }
          ]}
          placeholder={message('patient.search_patient')}
          handleSearch={handleSearch}
        />

        {patientsFound.length > 0 && (
          <Stack width="100%" overflow="auto" marginTop="1rem">
            <SearchResultsMetrics
              foundPatients={patients?.metadata?.total || patientsFound?.length}
              clearSearchedPatients={clearSearchedPatient}
            />
            <PatientsList patients={patientsFound} onAccessPatient={navigateToPatient} />
          </Stack>
        )}

        <Divider borderColor={theme.colors.colorDCE3ED} marginTop="1.5rem" marginBottom="1.5rem" />

        <Default show={isIdle} />
        <SearchError error={error} />
        <Loading show={isLoading} />

        {patients?.metadata && (
          <Pagination
            pageNumber={pageRef.current}
            totalPages={Math.ceil(patients.metadata.total / patients.metadata.limit)}
            setPageNumber={handlePage}
          />
        )}
      </Container>
    </PortalModal>
  )
}
